import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface DersOzet {
  ders_id: string;
  ders_adi: string;
  salon_adi: string;
  egitmenler: string;
  kadin_gelecek: number;
  kadin_gelemez: number;
  kadin_cevapsiz: number;
  erkek_gelecek: number;
  erkek_gelemez: number;
  erkek_cevapsiz: number;
  toplam: number;
  dengeli: boolean;
}

interface GunlukOzet {
  tarih: string;
  dersler: DersOzet[];
}

const CEVAP_RENK: Record<string, string> = {
  evet: '#085041',
  hayir: '#72243E',
  cevapsiz: '#666',
};

function CinsiyetBadge({ kadin, erkek, cevapsiz, renk }: { kadin: number; erkek: number; cevapsiz: number; renk?: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <span style={{ fontSize: 12 }}>👩 {kadin}</span>
      <span style={{ color: '#ccc' }}>·</span>
      <span style={{ fontSize: 12 }}>👨 {erkek}</span>
      {cevapsiz > 0 && (
        <>
          <span style={{ color: '#ccc' }}>·</span>
          <span style={{ fontSize: 12, color: '#999' }}>? {cevapsiz}</span>
        </>
      )}
    </div>
  );
}

const Katilim: React.FC = () => {
  const { kullanici } = useAuth();
  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const borderSub   = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';
  const [tarih, setTarih] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [veri, setVeri] = useState<DersOzet[]>([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [detayDers, setDetayDers] = useState<any>(null);
  const [detayListe, setDetayListe] = useState<any[]>([]);
  const [detayYukleniyor, setDetayYukleniyor] = useState(false);
  const [subeler, setSubeler] = useState<any[]>([]);
  const [subeId, setSubeId] = useState('');

  useEffect(() => {
    if (kullanici?.rol === 'super_admin') {
      api.get('/api/subeler').then(r => setSubeler(r.data || [])).catch(() => {});
    }
  }, [kullanici]);

  useEffect(() => {
    yukle();
  }, [tarih, subeId]);

  async function yukle() {
    setYukleniyor(true);
    setVeri([]);
    try {
      const params: any = { tarih };
      if (subeId) params.sube_id = subeId;
      const res = await api.get('/api/katilim/admin/gunluk', { params });
      setVeri(Array.isArray(res.data) ? res.data : (res.data?.dersler || []));
    } catch {
    } finally {
      setYukleniyor(false);
    }
  }

  async function detayAc(ders: DersOzet) {
    setDetayDers(ders);
    setDetayListe([]);
    setDetayYukleniyor(true);
    try {
      const res = await api.get(`/api/katilim/ozet/${ders.ders_id}`, { params: { tarih } });
      setDetayListe(res.data?.liste || []);
    } catch {
    } finally {
      setDetayYukleniyor(false);
    }
  }

  const dersler = veri;
  const toplamGelecek = dersler.reduce((s, d) => s + Number(d.kadin_gelecek || 0) + Number(d.erkek_gelecek || 0), 0);
  const toplamGelemez = dersler.reduce((s, d) => s + Number((d as any).gelemez || 0), 0);
  const toplamCevapsiz = dersler.reduce((s, d) => s + Number((d as any).cevapsiz || 0), 0);
  const dengesizSayi = dersler.filter(d => !d.dengeli).length;

  return (
    <div style={{ padding: 28, maxWidth: 960, margin: '0 auto' }}>
      <h2 style={{ margin: 0, marginBottom: 6, fontSize: 20, fontWeight: 700 }}>Katılım Teyit</h2>
      <p style={{ color: textSec, fontSize: 13, margin: 0, marginBottom: 24 }}>
        Öğrencilerin ders katılım cevapları
      </p>

      {/* Filtreler */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <label style={{ fontSize: 12, color: isDark ? '#999' : '#666', display: 'block', marginBottom: 4 }}>Tarih</label>
          <input
            type="date"
            value={tarih}
            onChange={e => setTarih(e.target.value)}
            style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: '7px 12px', fontSize: 14 }}
          />
        </div>
        {kullanici?.rol === 'super_admin' && subeler.length > 0 && (
          <div>
            <label style={{ fontSize: 12, color: isDark ? '#999' : '#666', display: 'block', marginBottom: 4 }}>Şube</label>
            <select
              value={subeId}
              onChange={e => setSubeId(e.target.value)}
              style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: '7px 12px', fontSize: 14 }}
            >
              <option value="">Tümü</option>
              {subeler.map((s: any) => (
                <option key={s.id} value={s.id}>{s.ad}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Özet kartlar */}
      {!yukleniyor && dersler.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Gelecek', deger: toplamGelecek, renk: '#085041', bg: isDark ? 'rgba(8,80,65,0.2)' : '#E1F5EE' },
            { label: 'Gelemez', deger: toplamGelemez, renk: '#72243E', bg: isDark ? 'rgba(114,36,62,0.2)' : '#FBEAF0' },
            { label: 'Cevapsız', deger: toplamCevapsiz, renk: isDark ? '#999' : '#666', bg: isDark ? '#1c1c1c' : '#f5f5f5' },
            { label: 'Dengesiz Ders', deger: dengesizSayi, renk: '#b45309', bg: isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb' },
          ].map(k => (
            <div key={k.label} style={{ background: k.bg, borderRadius: 12, padding: '16px 20px' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: k.renk }}>{k.deger}</div>
              <div style={{ fontSize: 12, color: isDark ? '#999' : '#666', marginTop: 4 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tablo */}
      {yukleniyor ? (
        <div style={{ textAlign: 'center', padding: 60, color: textSec }}>Yükleniyor...</div>
      ) : dersler.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: textMuted, fontSize: 14 }}>
          {tarih} için kayıt bulunamadı
        </div>
      ) : (
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: cardAlt }}>
                {['Ders', 'Salon / Eğitmen', 'Kadın', 'Erkek', 'Cevapsız', 'Durum'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: textSec, fontWeight: 500, fontSize: 11, letterSpacing: 0.5, borderBottom: `1px solid ${border}` }}>
                    {h.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dersler.map((d, i) => {
                const fark = Math.abs(d.kadin_gelecek - d.erkek_gelecek);
                return (
                  <tr
                    key={d.ders_id}
                    onClick={() => detayAc(d)}
                    style={{ cursor: 'pointer', background: detayDers?.ders_id === d.ders_id ? isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE' : i % 2 === 0 ? card : cardAlt }}
                    onMouseEnter={e => (e.currentTarget.style.background = isDark ? '#252535' : '#f5f5ff')}
                    onMouseLeave={e => (e.currentTarget.style.background = detayDers?.ders_id === d.ders_id ? isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE' : i % 2 === 0 ? card : cardAlt)}
                  >
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: textPrimary }}>{d.ders_adi}</td>
                    <td style={{ padding: '12px 14px', color: isDark ? '#999' : '#666' }}>
                      <div>{d.salon_adi || '—'}</div>
                      <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>{d.egitmenler || '—'}</div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span style={{ color: '#085041', fontWeight: 600 }}>{Number(d.kadin_gelecek || 0)} ✓</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span style={{ color: '#085041', fontWeight: 600 }}>{Number(d.erkek_gelecek || 0)} ✓</span>
                        {Number((d as any).gelemez || 0) > 0 && <span style={{ color: '#72243E' }}>{Number((d as any).gelemez)} ✗</span>}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', color: textSec }}>
                      {Number((d as any).cevapsiz || 0)}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {d.dengeli ? (
                        <span style={{ background: isDark ? 'rgba(16,185,129,0.15)' : '#E1F5EE', color: '#085041', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>Dengeli</span>
                      ) : (
                        <span style={{ background: isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb', color: '#b45309', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>⚠ +{fark} fark</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detay paneli */}
      {detayDers && (
        <div style={{ marginTop: 24, background: card, borderRadius: 12, border: `1px solid ${border}`, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${border}` }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{detayDers.ders_adi} — Öğrenci Listesi</div>
              <div style={{ color: textSec, fontSize: 12, marginTop: 2 }}>
                👩 {detayDers.kadin_gelecek} gelecek · 👨 {detayDers.erkek_gelecek} gelecek
              </div>
            </div>
            <button
              onClick={() => setDetayDers(null)}
              style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#aaa' }}
            >
              ✕
            </button>
          </div>
          {detayYukleniyor ? (
            <div style={{ textAlign: 'center', padding: 32, color: textSec }}>Yükleniyor...</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: cardAlt }}>
                  {['Öğrenci', 'Cinsiyet', 'Cevap'].map(h => (
                    <th key={h} style={{ padding: '8px 16px', textAlign: 'left', color: textSec, fontWeight: 500, fontSize: 11, letterSpacing: 0.5, borderBottom: `1px solid ${border}` }}>
                      {h.toUpperCase()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detayListe.map((o, i) => (
                  <tr key={o.ogrenci_id} style={{ background: i % 2 === 0 ? card : cardAlt }}>
                    <td style={{ padding: '10px 16px', fontWeight: 500, color: textPrimary }}>{o.ad} {o.soyad}</td>
                    <td style={{ padding: '10px 16px', color: isDark ? '#999' : '#666' }}>
                      {o.cinsiyet === 'kadin' ? '👩 Kadın' : o.cinsiyet === 'erkek' ? '👨 Erkek' : '—'}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{
                        background: o.cevap === 'evet' ? isDark ? 'rgba(16,185,129,0.15)' : '#E1F5EE' : o.cevap === 'hayir' ? isDark ? 'rgba(228,75,74,0.15)' : '#FBEAF0' : isDark ? '#1c1c1c' : '#f5f5f5',
                        color: o.cevap === 'evet' ? '#085041' : o.cevap === 'hayir' ? '#72243E' : textSec,
                        borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600,
                      }}>
                        {o.cevap === 'evet' ? '✓ Gelecek' : o.cevap === 'hayir' ? '✗ Gelemez' : '? Cevap Yok'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default Katilim;
